/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import * as React from "react";

import { AlertDialog } from "@/shared/components/AlertDialog";
import { RobotoDivider } from "@/shared/components/RobotoDivider";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import { formatByteSize } from "@/shared/text";
import { APIServiceError, LimitError } from "@/types";
import { Resource } from "@/types/resources";

export interface RobotoErrorAlertContentProps {
  error: APIServiceError | undefined;
  formatContent?: (
    error: APIServiceError | undefined,
    defaultFormatter: (error: APIServiceError) => React.ReactNode,
  ) => React.ReactNode;
  detailMode?: "off" | "system_user_only" | "on"; // Defaults to "system_user_only"
}

export interface RobotoErrorAlertDialogProps
  extends RobotoErrorAlertContentProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: (error: APIServiceError | undefined) => string;
}

interface HumanReadableLimit {
  name: string;
  description?: string;
  currentQuantity: string;
  limitQuantity: string;
}

/**
 * Makes a Roboto service limit error human-readable.
 */
const formatLimitError = (error: LimitError): HumanReadableLimit => {
  let name: string = error.resourceName;
  let description: string | undefined = undefined;
  let currentQuantity: string = error.currentQuantity.toString();
  let limitQuantity: string = error.limitQuantity.toString();

  switch (error.resourceName) {
    case Resource.ActionCount:
      name = "Total Actions";
      description = "The total number of actions created in this org.";
      break;
    case Resource.DatasetCount:
      name = "Total Datasets";
      description = "The total number of datasets in this org.";
      break;
    case Resource.InvocationComputeCreditMillionths:
      name = "Used Compute Credits";
      description =
        "The amount of action compute used by this org in a monthly billing period.";
      currentQuantity = (error.currentQuantity / 1000000).toFixed(2);
      limitQuantity = (error.limitQuantity / 1000000).toFixed(2);
      break;
    case Resource.InvocationsScheduled:
      name = "Invocations Scheduled";
      description =
        "The number of invocations scheduled by this org in a monthly billing period";
      break;
    case Resource.ManagedDataDownloadBytes:
      name = "Data Downloaded";
      description =
        "The total data downloaded by this org in a monthly billing period. " +
        "This does not include downloads performed by actions in the platform. ";
      currentQuantity = formatByteSize(error.currentQuantity);
      limitQuantity = formatByteSize(error.limitQuantity);
      break;
    case Resource.ManagedDataStoredBytes:
      name = "Total Storage";
      description = "The total amount of data stored by this org.";
      currentQuantity = formatByteSize(error.currentQuantity);
      limitQuantity = formatByteSize(error.limitQuantity);
      break;
    case Resource.OrgMembers:
      name = "Total Org Members";
      description = "The number of members in this org.";
      break;
    case Resource.OwnedOrgsPerUser:
      name = "Orgs Owned per User";
      description = "The number of orgs you are an owner of.";
      break;
    case Resource.TriggersActive:
      name = "Active Triggers";
      description =
        "The number of active triggers in this org. Other triggers may exist but are disabled.";
      break;
  }

  return {
    name: name,
    description: description,
    currentQuantity: currentQuantity,
    limitQuantity: limitQuantity,
  };
};

interface LimitErrorContentProps {
  error: LimitError;
}
const LimitErrorContent: React.FC<LimitErrorContentProps> = (props) => {
  const { name, currentQuantity, limitQuantity, description } =
    formatLimitError(props.error);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const popoverOpen = Boolean(anchorEl);

  return (
    <>
      <RobotoTypography variant={"body1"}>
        You&lsquo;ve exceeded a service limit:{" "}
        <b
          aria-owns={popoverOpen ? "mouse-over-popover" : undefined}
          aria-haspopup="true"
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
        >
          {name} ({props.error.resourceName})
        </b>
        .<br />
        <br />
        Your limit is set at <b>{limitQuantity}</b>, and you&lsquo;re currently
        at <b>{currentQuantity}</b>.
        <br />
        <br />
        To increase this limit, please reach out to{" "}
        <Link underline="hover" href={"mailto:support@roboto.ai"}>
          support@roboto.ai
        </Link>
      </RobotoTypography>
      {description && (
        <Popover
          id="mouse-over-popover"
          sx={{
            pointerEvents: "none",
          }}
          open={popoverOpen}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <Box
            sx={{
              overflow: "auto",
              padding: theme.spacing(1),
            }}
          >
            <RobotoTypography variant={"caption"}>
              {description}
            </RobotoTypography>
          </Box>
        </Popover>
      )}
    </>
  );
};

const standardErrorContent = (error: APIServiceError): React.ReactNode => {
  return <RobotoTypography variant={"body2"}>{error.message}</RobotoTypography>;
};

export const RobotoErrorAlertContent: React.FC<RobotoErrorAlertContentProps> = (
  props,
) => {
  const theme = useTheme();

  let coreContent: React.ReactNode;
  let details: React.ReactNode | undefined;
  const detailMode = props.detailMode || "system_user_only";

  if (!props.error) {
    return <></>;
  }

  if (detailMode !== "off") {
    details = (
      <>
        <RobotoDivider />
        <Accordion>
          <AccordionSummary>
            <RobotoTypography variant={"body2"}>Stack Trace</RobotoTypography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                background: theme.palette.code.main,
                overflow: "auto",
                padding: theme.spacing(1.5),
                mt: theme.spacing(1),
              }}
            >
              <Typography
                component="span"
                variant="body2"
                style={{
                  whiteSpace: "pre-wrap",
                  color: theme.palette.text.secondary,
                  fontSize: "0.8rem",
                }}
              >
                <pre>{props.error?.stackTrace}</pre>
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </>
    );

    if (detailMode === "system_user_only") {
      details = <SystemUserOnly>{details}</SystemUserOnly>;
    }
  }

  if (!props.error) {
    coreContent = props.formatContent ? (
      props.formatContent(props.error, () => {
        return <></>;
      })
    ) : (
      <></>
    );
  } else if (props.error instanceof LimitError) {
    coreContent = props.formatContent ? (
      props.formatContent(props.error, (error) => {
        return <LimitErrorContent error={error as LimitError} />;
      })
    ) : (
      <LimitErrorContent error={props.error} />
    );
  } else {
    coreContent = props.formatContent
      ? props.formatContent(props.error, standardErrorContent)
      : standardErrorContent(props.error);
  }

  return (
    <>
      {coreContent}
      {details}
    </>
  );
};

const titleFromLimitError = (error: LimitError): string => {
  const { name } = formatLimitError(error);
  return `Limit Exceeded: ${name}`;
};

export const RobotoErrorAlertDialog: React.FC<RobotoErrorAlertDialogProps> = (
  props,
) => {
  let title: string;
  if (props.title) {
    title = props.title(props.error);
  } else if (!props.error) {
    title = "No Error";
  } else if (props.error instanceof LimitError) {
    title = titleFromLimitError(props.error);
  } else {
    title = props.error.errorCode || "RobotoUnknownOperationError";
  }

  return (
    <AlertDialog
      dialogOpen={props.open}
      handleClose={() => props.setOpen(false)}
      dialogTitle={title}
      dialogContent={<RobotoErrorAlertContent {...props} />}
    />
  );
};
